// -- src/firebase.js

// Import the functions you need from the SDKs you need
import {
  getAnalytics,
  isSupported as isAnalyticsSupported,
} from "firebase/analytics";
import { initializeApp } from "firebase/app";
import {
  browserLocalPersistence,
  browserSessionPersistence,
  connectAuthEmulator,
  indexedDBLocalPersistence,
  inMemoryPersistence,
  initializeAuth,
} from "firebase/auth";
import { connectFirestoreEmulator, getFirestore } from "firebase/firestore";
import { connectFunctionsEmulator, getFunctions } from "firebase/functions";
import { connectStorageEmulator, getStorage } from "firebase/storage";

import { firebaseConfig as prodConfig } from "./firebase.config.prod";

// Use prod in production builds, otherwise use test/dev config
const firebaseConfig = prodConfig;

// TODO: Add SDKs for Firebase products that you want to use
// https://firebase.google.com/docs/web/setup#available-libraries

const isIndexedDbAvailable = () => {
  try {
    const root = typeof window !== "undefined" ? window : null;

    return typeof root?.indexedDB !== "undefined" && Boolean(root.indexedDB);
  } catch (error) {
    return false;
  }
};

export const getAuthPersistenceStack = () => {
  const persistence = [];

  if (isIndexedDbAvailable()) {
    persistence.push(indexedDBLocalPersistence);
  }

  persistence.push(
    browserLocalPersistence,
    browserSessionPersistence,
    inMemoryPersistence,
  );

  return persistence;
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const auth = initializeAuth(app, {
  persistence: getAuthPersistenceStack(),
  // Avoid Firebase Auth eagerly loading its hidden gapi iframe on public
  // Safari/mobile pages. Google popup handlers opt into the resolver directly.
  popupRedirectResolver: undefined,
});
let analytics = null;
let analyticsInitializationPromise = null;

const isLocalhost = () => {
  const host = String(window.location?.hostname || "").toLowerCase();
  return host === "localhost" || host === "127.0.0.1" || host === "[::1]";
};

const scheduleAfterFirstPaint = (callback) => {
  const scheduleIdle = () => {
    window.setTimeout(() => {
      if ("requestIdleCallback" in window) {
        window.requestIdleCallback(callback, { timeout: 2500 });
        return;
      }
      callback();
    }, 3000);
  };

  if (document.readyState === "complete") {
    scheduleIdle();
    return;
  }

  window.addEventListener("load", scheduleIdle, { once: true });
};

const analyticsIsEligible =
  typeof window !== "undefined" && !isLocalhost();

export const getAnalyticsWhenReady = () => {
  if (!analyticsIsEligible) return Promise.resolve(null);
  if (analytics) return Promise.resolve(analytics);
  if (analyticsInitializationPromise) return analyticsInitializationPromise;

  analyticsInitializationPromise = isAnalyticsSupported()
    .then((isSupported) => {
      if (isSupported) {
        analytics = getAnalytics(app);
      }
      return analytics;
    })
    .catch((error) => {
      analytics = null;
      analyticsInitializationPromise = null;
      throw error;
    });
  return analyticsInitializationPromise;
};

if (analyticsIsEligible) {
  scheduleAfterFirstPaint(() => {
    void getAnalyticsWhenReady();
  });
}

// Firebase that can also be emulated
let db, functions, storage;

// Enable Firestore debug logging
// setLogLevel('debug');

if (process.env.NODE_ENV === "development") {
  // Initialize services with emulator in development
  connectAuthEmulator(auth, "http://localhost:9099/");
  db = getFirestore();
  connectFirestoreEmulator(db, "localhost", 8080);
  functions = getFunctions(app);
  connectFunctionsEmulator(functions, "127.0.0.1", 5001);
  storage = getStorage();
  connectStorageEmulator(storage, "127.0.0.1", 9199);
} else {
  // Initialize services normally for production
  db = getFirestore();
  functions = getFunctions(app);
  storage = getStorage();
}

export { analytics, app, auth, db, functions, storage };
